import { ReactElement, MouseEvent } from 'react';

import { Action } from '@hh.ru/magritte-ui-action';
import { Avatar } from '@hh.ru/magritte-ui-avatar';
import { Divider } from '@hh.ru/magritte-ui-divider';
import {
    CrossOutlinedSize24,
    CheckCircleOutlinedSize16,
    ExclamationTriangleOutlinedSize16,
    TrashOutlinedSize24,
} from '@hh.ru/magritte-ui-icon/icon';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import { FileName } from '@hh.ru/magritte-ui-upload/FileName';
import { UploadType, LoadedFile, RenderFileInfo } from '@hh.ru/magritte-ui-upload/types';
import { getUploadIcon, bytes2mb } from '@hh.ru/magritte-ui-upload/utils';

import styles from './file-view.less';

export interface FileViewProps {
    uploadType: UploadType;
    loadedFile: LoadedFile;
    renderFileInfo: RenderFileInfo;
    onFileDelete: (event: MouseEvent) => void;
    preview: string;
    'aria-label': string;
    isLast?: boolean;
}

const FileView = ({
    uploadType,
    loadedFile,
    preview,
    renderFileInfo,
    onFileDelete,
    'aria-label': ariaLabel,
    isLast = false,
}: FileViewProps): ReactElement => {
    const isLoading = Boolean(loadedFile?.loading);
    const isInvalid = Boolean(loadedFile?.error);
    const isImageType = uploadType === 'image';
    return (
        <>
            <Avatar
                mode={isImageType && !isLoading && !isInvalid ? 'image' : 'icon'}
                style="secondary"
                image={preview}
                size={48}
                icon={getUploadIcon(uploadType, loadedFile, true)}
                aria-label={ariaLabel}
            />
            <div className={styles.fileInfoBody}>
                <div className={styles.fileInfoAndControls}>
                    <div className={styles.fileInfoContainer}>
                        <FileName file={loadedFile.data} />
                        <VSpacing default={4} />
                        <div className={styles.fileInfo}>
                            {isInvalid && <ExclamationTriangleOutlinedSize16 initial="negative" />}
                            {isLoading && <Loader size={16} />}
                            {!isInvalid && !isLoading && <CheckCircleOutlinedSize16 initial="positive" />}
                            {renderFileInfo({
                                ...loadedFile,
                                fileSizeMB: bytes2mb(loadedFile.data.size),
                            })}
                        </div>
                    </div>
                    <div className={styles.controls}>
                        {isLoading && <Action icon={CrossOutlinedSize24} mode="secondary" onClick={onFileDelete} />}
                        {!isLoading && (
                            <Action
                                icon={TrashOutlinedSize24}
                                mode="secondary"
                                onClick={onFileDelete}
                                data-qa="magritte-upload-delete-file-action"
                            />
                        )}
                    </div>
                </div>
                {!isLast && (
                    <div>
                        <VSpacing default={16} />
                        <Divider />
                        <VSpacing default={16} />
                    </div>
                )}
            </div>
        </>
    );
};

export { FileView };
